import React from "react";
import A4PageFrame from "./A4PageFrame";
import { ROWS_PER_PAGE_LANDSCAPE, ROW_HEIGHT } from "./pageMetrics";
import { fmtNum, fmtDate } from "./formatters";

// Phase 3 (B2): Surfaces list — mirrors PDF pages 2–4.
//
// Each row is a lease (object). Modules are visually grouped with a subtotal row.
// Both full m² and pro-rated m² are shown (per client decision Q1) — the column
// labelled "Surface" is the full unit m² (when imported via A9's Full Surface
// column) and "Surface m²" is the pro-rated value.
//
// Pagination: ~28 rows per A4 page including subtotals. The component renders
// one A4PageFrame per page slice and stamps the right pageNumber.
//
// Props
//   data        response from GET /v1/repartition-report/surfaces
//   building    { name, address }
//   periodLabel string
//   whiteLabelling, logoUrl, basePageNumber (starting page), grandTotalPages
//                used by the orchestrator to keep page X/N continuous across sections

// Plaines-6 feedback (latest): the client asked to REMOVE the "Répartition des
// surfaces" donut from this report (it stays only on the Modules page) and to
// use the freed space for more rows. With the donut gone the whole content area
// is available for the table, so rows-per-page is derived from the calibrated
// page geometry (see pageMetrics.js) — landscape = 22 rows — instead of the old
// hand-tuned 18. Single source of truth shared with the Preview.js mirror.
const ROWS_PER_PAGE = ROWS_PER_PAGE_LANDSCAPE;

const SurfacesList = ({
  data,
  building,
  periodLabel,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const surfaces = data?.surfaces || [];
  const subtotals = data?.subtotals || [];
  const grandTotal = data?.grand_total || 0;

  // Build a flat list of rendered rows in the order they appear in the PDF: each
  // module's leases followed by a "Total <module>" subtotal row, then the final
  // "Total général" at the very end.
  const flatRows = buildFlatRows(surfaces, subtotals, grandTotal);

  // Paginate
  const pages = [];
  for (let i = 0; i < flatRows.length; i += ROWS_PER_PAGE) {
    pages.push(flatRows.slice(i, i + ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  return (
    <>
      {pages.map((rows, pageIdx) => (
        <A4PageFrame
          key={pageIdx}
          building={building}
          periodLabel={periodLabel}
          sectionTitle={`Surfaces${pages.length > 1 ? ` (${pageIdx + 1}/${pages.length})` : ""}`}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={basePageNumber + pageIdx}
          totalPages={grandTotalPages}
          landscape
        >
          <table style={tbl}>
            {/* M1.3: column structure aligned to the standardised report layout
                confirmed by the client on 10.06.2026 — 11 columns, in this
                exact order. Dropped: Immeuble, Début loc. Added: Genre d'objet.
                Reordered: Objet (3rd) before Locataire (4th) before Référence
                (5th). Widths sum to ~990 px which fits within landscape A4
                (~1080 usable).
                M1.5d: client asked to widen Objet so the full value is visible
                (e.g. "2689.01.0001.02_v"). Locataire was the widest column at
                220 px so it absorbed most of the redistribution (long tenant
                names still truncate cleanly with the existing ellipsis style).
                Étage bumped a touch to keep "rez-de-chaussée" intact.
                Column widths (in order): Numéro 50, Type 90, Objet 140,
                Locataire 145, Référence 80, Genre d'objet 95, Étage 95,
                Surface 65, Surface m² 88, FEE début 75, FEE fin 75.
                Client 2026-07-22: grand-total Surface m² ("106'788.37") was
                clipping to "106'788.3..." — Surface m² widened 70→88 and the
                18 px reclaimed from Locataire (150→145) + Type nudge / the
                small spare landscape band. Total colgroup 998 px still inside
                the ~1059 px landscape content area.
                NOTE: <colgroup> must contain ONLY <col> elements — no comments
                or whitespace text nodes between them (React forbids it). */}
            <colgroup>
              <col style={{ width: 50 }} />
              <col style={{ width: 85 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 145 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 95 }} />
              <col style={{ width: 95 }} />
              <col style={{ width: 65 }} />
              <col style={{ width: 88 }} />
              <col style={{ width: 75 }} />
              <col style={{ width: 80 }} />
            </colgroup>
            <thead>
              <tr style={{ borderBottom: "1px solid #00033F" }}>
                <th style={th}>Numéro</th>
                <th style={th}>Type</th>
                <th style={th}>Objet</th>
                <th style={th}>Locataire</th>
                <th style={th}>Référence</th>
                <th style={th}>Genre d&apos;objet</th>
                <th style={th}>Étage</th>
                <th style={thNum}>Surface</th>
                <th style={thNum}>Surface m²</th>
                <th style={thNum}>FEE début</th>
                <th style={thNum}>FEE fin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                if (r.type === "subtotal") {
                  // 11-col total row: blank, colSpan=6 label, blank Surface,
                  // total Surface m², two blank FEE cells.
                  return (
                    <tr key={i} style={{ fontWeight: 700, borderTop: "1px solid rgba(0,3,63,0.2)" }}>
                      <td style={td} />
                      <td style={td} colSpan={6}>Total {r.module_name}</td>
                      <td style={tdNum} />
                      <td style={tdNum}>{fmtNum(r.total)}</td>
                      <td style={tdNum} />
                      <td style={tdNum} />
                    </tr>
                  );
                }
                if (r.type === "grand") {
                  return (
                    <tr key={i} style={{ fontWeight: 700, borderTop: "2px solid #00033F" }}>
                      <td style={td} />
                      <td style={td} colSpan={6}>Total général</td>
                      <td style={tdNum} />
                      <td style={tdNum}>{fmtNum(r.total)}</td>
                      <td style={tdNum} />
                      <td style={tdNum} />
                    </tr>
                  );
                }
                const s = r.surface;
                return (
                  <tr key={i}>
                    <td style={td}>{s.module_code}</td>
                    <td style={td}>{s.module_name}</td>
                    <td style={td}>{s.objet_code || ""}</td>
                    <td style={td}>{s.tenant_name || "—"}</td>
                    <td style={td}>{s.reference}</td>
                    <td style={td}>{s.genre_objet || ""}</td>
                    <td style={td}>{s.etage || ""}</td>
                    <td style={tdNum}>{s.full_surface_value != null ? fmtNum(s.full_surface_value) : "-"}</td>
                    <td style={tdNum}>{fmtNum(s.prorated_surface_value)}</td>
                    <td style={tdNum}>{fmtDate(s.fee_start)}</td>
                    <td style={tdNum}>{fmtDate(s.fee_end)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </A4PageFrame>
      ))}
    </>
  );
};

function buildFlatRows(surfaces, subtotals, grandTotal) {
  // Group surfaces by module_code, preserving the API's ordering.
  const groups = new Map();
  for (const s of surfaces) {
    const key = String(s.module_code);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  const subtotalByModule = new Map(subtotals.map((st) => [String(st.module_code), st]));

  const out = [];
  for (const [moduleCode, group] of groups.entries()) {
    for (const s of group) out.push({ type: "row", surface: s });
    const st = subtotalByModule.get(moduleCode);
    if (st) out.push({ type: "subtotal", module_name: st.module_name, total: st.total });
  }
  out.push({ type: "grand", total: grandTotal });
  return out;
}

// M1.2: switched from tableLayout:auto to tableLayout:fixed so the 12-col
// table respects the <colgroup> widths above and stops overflowing the A4
// frame on long tenant names. overflow:hidden + textOverflow on cells means
// values that exceed the column truncate cleanly with "…" rather than
// pushing the page wider.
// marginBottom:0 overrides Bootstrap Reboot's `table{margin-bottom:1rem}` so the
// fixed content region keeps its full height (see pageMetrics.js).
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 0, tableLayout: "fixed" };
// M1.1: bumped from 8/7.5pt to 11/10pt per client feedback ("size 9, little
// bigger"). Body 10pt keeps 12 columns + nowrap inside A4 width because the
// per-cell padding stays tight; if a future client adds longer tenant names we
// may need to shrink labelled columns or drop padding to 1px.
const th = { padding: "3px 4px", fontWeight: 600, textAlign: "left", fontSize: 11, whiteSpace: "nowrap" };
const thNum = { ...th, textAlign: "right" };
// M1.2: nowrap + overflow:hidden + ellipsis lets long values shrink instead of
// pushing the column wider. Title attribute on the rendered <td> is not set
// (html-to-image PDF capture is what matters; hover tooltips don't apply).
// M1.5b: explicit `lineHeight` + larger vertical padding so row heights are
// (a) deterministic regardless of PP Neue Montreal's intrinsic line metrics
// — prevents trailing-row clipping — and (b) visually comfortable. Row total
// = 5 + 14 + 5 + 1(border) = 25 px → 22 rows × 25 + 26 px chrome = 576 px
// in 658 px landscape content area (~87 % page fill, ~13 % safety margin).
const td = {
  // Row height PINNED to ROW_HEIGHT.landscape so subtotal/grand rows can't drift
  // taller and push the last row under the footer (recurring clipping bug).
  boxSizing: "border-box",
  height: ROW_HEIGHT.landscape,
  padding: "5px 4px",
  fontSize: 10,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default SurfacesList;
